import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';
import {
  ProcessSection,
  ProcessTitle,
  ProcessFlow,
  StepContainer,
  StepIconContainer,
  StepIcon,
  StepContent,
  StepTitle,
  StepDescription,
  DataParticle,
  TechCircle,
  StepCorner,
  DataPoint
} from '../../components/ProcessFlowStyles';
import { FaUserCheck, FaQuestionCircle, FaCalendarAlt, FaBell, FaRedo } from 'react-icons/fa';

// Animações
const glitchText = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-5px);
    text-shadow: 0 0 0 rgba(0, 0, 0, 0);
    clip-path: inset(20% 0 0 20%);
  }
  10% {
    opacity: 0.8;
    transform: translateX(5px);
    text-shadow: -2px 0 #64B6FF, 2px 0 #00FFFF;
    clip-path: inset(0 20% 20% 0);
  }
  15% {
    opacity: 0.4;
    transform: translateX(-3px);
    text-shadow: 2px 0 #64B6FF, -2px 0 #00FFFF;
    clip-path: inset(20% 0 0 0);
  }
  20% {
    opacity: 1;
    transform: translateX(2px);
    text-shadow: -1px 0 #64B6FF, 1px 0 #00FFFF;
    clip-path: inset(0 0 20% 0);
  }
  25% {
    opacity: 0.7;
    transform: translateX(0);
    text-shadow: 1px 0 #64B6FF, -1px 0 #00FFFF;
    clip-path: inset(0 0 0 20%);
  }
  30%, 100% {
    opacity: 1;
    transform: translateX(0);
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.8);
    clip-path: inset(0 0 0 0);
  }
`;

const glitchAppear = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9) skewX(5deg);
    box-shadow: 0 0 10px #00FFFF;
    background: rgba(0, 10, 20, 0.9);
    clip-path: inset(50% 50% 50% 50%);
  }
  15% {
    opacity: 0.3;
    transform: scale(0.95) skewX(-3deg);
    box-shadow: 0 0 15px #64B6FF;
    background: rgba(0, 20, 40, 0.8);
    clip-path: inset(10% 40% 40% 10%);
  }
  30% {
    opacity: 0.6;
    transform: scale(1.05) skewX(2deg);
    box-shadow: 0 0 5px #00FFFF;
    background: rgba(0, 15, 30, 0.9);
    clip-path: inset(40% 10% 10% 40%);
  }
  45% {
    opacity: 0.8;
    transform: scale(0.98) skewX(-1deg);
    box-shadow: 0 0 8px #64B6FF;
    background: rgba(0, 10, 20, 0.8);
    clip-path: inset(20% 20% 20% 20%);
  }
  60%, 100% {
    opacity: 1;
    transform: scale(1) skewX(0);
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
    background: rgba(0, 10, 20, 0.8);
    clip-path: inset(0 0 0 0);
  }
`;

const iconGlitch = keyframes`
  0% {
    opacity: 0;
    filter: blur(10px) hue-rotate(0deg);
    transform: scale(0.5);
    border-radius: 50%;
    box-shadow: 0 0 0 0 rgba(0, 255, 255, 0);
  }
  15% {
    opacity: 0.5;
    filter: blur(5px) hue-rotate(90deg);
    transform: scale(1.3) translateX(5px);
    border-radius: 40% 60% 60% 40%;
    box-shadow: 0 0 10px 2px rgba(0, 255, 255, 0.5);
  }
  30% {
    opacity: 0.7;
    filter: blur(0) hue-rotate(180deg);
    transform: scale(0.9) translateX(-5px);
    border-radius: 60% 40% 40% 60%;
    box-shadow: 0 0 5px 1px rgba(100, 182, 255, 0.5);
  }
  45% {
    opacity: 0.9;
    filter: blur(3px) hue-rotate(270deg);
    transform: scale(1.1) translateY(3px);
    border-radius: 40% 40% 60% 60%;
    box-shadow: 0 0 8px 3px rgba(0, 255, 255, 0.7);
  }
  60%, 100% {
    opacity: 1;
    filter: blur(0) hue-rotate(360deg);
    transform: scale(1);
    border-radius: 50%;
    box-shadow: 0 0 15px 5px rgba(100, 182, 255, 0.3);
  }
`;

const titleGlitch = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-20px);
    letter-spacing: -5px;
    filter: blur(5px);
    text-shadow: -2px 0 #64B6FF, 2px 0 #00FFFF;
  }
  15% {
    opacity: 0.7;
    transform: translateX(15px);
    letter-spacing: 10px;
    filter: blur(0);
    text-shadow: 2px 0 #64B6FF, -2px 0 #00FFFF;
  }
  30% {
    opacity: 0.9;
    transform: translateX(-10px);
    letter-spacing: 3px;
    filter: blur(3px);
    text-shadow: -1px 0 #64B6FF, 1px 0 #00FFFF;
  }
  45% {
    opacity: 1;
    transform: translateX(5px);
    letter-spacing: 1px;
    filter: blur(0);
    text-shadow: 1px 0 #64B6FF, -1px 0 #00FFFF;
  }
  60%, 100% {
    opacity: 1;
    transform: translateX(0);
    letter-spacing: 0;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  }
`;

const digitalScanlines = keyframes`
  0% {
    background-position: 0 0;
    opacity: 0.05;
  }
  50% {
    background-position: 100% 100%;
    opacity: 0.1;
  }
  100% {
    background-position: 0 0;
    opacity: 0.05;
  }
`;

// Animação de transição para miniatura
const toMiniature = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
    z-index: 20;
  }
  100% {
    transform: translate(var(--target-x), var(--target-y)) scale(0.3);
    z-index: 1;
  }
`;

// Animação de entrada em tela cheia
const fromFullscreen = keyframes`
  0% {
    transform: scale(0.1);
    opacity: 0;
    filter: blur(10px);
  }
  50% {
    transform: scale(1.05);
    opacity: 0.7;
    filter: blur(3px);
  }
  75% {
    transform: scale(0.98);
    opacity: 0.9;
    filter: blur(1px);
  }
  100% {
    transform: scale(1);
    opacity: 1;
    filter: blur(0px);
  }
`;

// Animação para mostrar todos os passos
const showAllSteps = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  background: transparent;
  cursor: pointer;
`;

// ScalableContent wrapper for proportional scaling
const ScalableContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform: scale(var(--scale-factor, 1.2)); /* Zoom reduzido de 1.5 para 1.2 */
  transform-origin: center center; /* Alterado para centralizar em ambas direções */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Centralizado verticalmente */
  margin-top: -30px; /* Valor negativo para subir o conteúdo */
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 2px;
  font-size: 1.8rem;
  text-transform: uppercase;
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  z-index: 30;
  
  &::before {
    content: '//';
    color: rgba(100, 182, 255, 0.5);
    margin-right: 10px;
  }
`;

const ContentPreview = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  margin-top: -15px;
  padding: 60px;
  background: transparent;
  border: none;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translateY(-10px);
  
  &::before, &::after {
    display: none;
  }
`;

const StyledProcessSection = styled(ProcessSection)`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: transparent;
  padding: 0;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-top: -1px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(0, 255, 255, 0.03), rgba(100, 182, 255, 0.03));
    animation: ${digitalScanlines} 10s linear infinite;
  }
`;

const TwoColumnProcessFlow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
  width: 100%;
  position: relative;
  margin-top: 20px;
  height: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FullscreenContainer = styled.div`
  position: absolute;
  width: 90%;
  height: 80%;
  top: 47%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
`;

const StyledStepContainer = styled(StepContainer)`
  visibility: visible;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  max-width: 100%;
  padding: 30px;
  margin-bottom: 0;
  position: absolute;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 15px;
  background: rgba(0, 10, 20, 0.8);
  overflow: hidden;
  transform-origin: center center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      45deg,
      rgba(0, 255, 255, 0.03) 0px,
      rgba(0, 255, 255, 0.03) 1px,
      transparent 1px,
      transparent 10px
    );
    pointer-events: none;
    transition: background 0.3s ease;
  }
  
  &.fullscreen {
    animation: ${fromFullscreen} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    width: 100%;
    height: 100%;
    z-index: 20;
    border-color: rgba(0, 255, 255, 0.7);
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.3);
    
    ${StepIconContainer} {
      transform: scale(1.2);
      box-shadow: 0 0 30px rgba(0, 255, 255, 0.5);
    }
    
    ${StepTitle} {
      font-size: 3em;
      margin-bottom: 0;
    }
    
    ${StepDescription} {
      font-size: 1.6em;
      line-height: 1.5;
      max-width: 90%;
      margin-top: -15px; 
    }
  }
  
  &.miniature {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    animation: ${toMiniature} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    opacity: 0.8;
    
    ${StepIconContainer} {
      width: 40px;
      height: 40px;
      min-width: 40px;
      margin-bottom: 5px;
    }
    
    ${StepIcon} {
      font-size: 20px;
    }
    
    ${StepTitle} {
      font-size: 1em;
      margin-bottom: 5px;
    }
    
    ${StepDescription} {
      font-size: 0.8em;
      display: none;
    }
    
    ${DataParticle}, ${DataPoint} {
      display: none;
    }
  }
  
  &.hidden {
    display: none;
  }
  
  ${StepIconContainer} {
    min-width: 70px;
    width: 70px;
    height: 70px;
    margin-bottom: 15px;
    opacity: 1;
    position: relative;
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    animation: ${iconGlitch} 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      box-shadow: 0 0 15px 5px rgba(0, 255, 255, 0.2);
      z-index: -1;
      transition: box-shadow 0.3s ease;
    }
  }
  
  ${StepIcon} {
    font-size: 30px;
    transition: color 0.3s ease, text-shadow 0.3s ease;
  }
  
  ${StepContent} {
    width: 100%;
    text-align: center;
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }
  
  ${StepTitle} {
    font-size: 1.5em;
    margin-bottom: 0;
    color: #64B6FF;
    opacity: 1;
    position: relative;
    display: inline-block;
    transition: color 0.3s ease, text-shadow 0.3s ease, letter-spacing 0.3s ease;
    animation: ${glitchText} 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    
    &::before {
      content: attr(data-text);
      position: absolute;
      left: 0;
      text-shadow: -2px 0 #00FFFF;
      top: 0;
      color: #64B6FF;
      overflow: hidden;
      clip: rect(0, 900px, 0, 0);
      transition: color 0.3s ease;
    }
    
    &::after {
      content: attr(data-text);
      position: absolute;
      left: 0;
      text-shadow: 2px 0 #64B6FF;
      top: 0;
      color: #00FFFF;
      overflow: hidden;
      clip: rect(0, 900px, 0, 0);
      transition: color 0.3s ease;
    }
  }
  
  ${StepDescription} {
    font-size: 1.1em;
    opacity: 1;
    line-height: 1.5;
    transition: color 0.3s ease;
    animation: ${glitchText} 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }
`;

const ProgressIndicator = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  color: rgba(100, 182, 255, 0.7);
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  z-index: 30;
`;

const GridContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(90deg, rgba(100, 182, 255, 0.03) 1px, transparent 1px),
    linear-gradient(0deg, rgba(100, 182, 255, 0.03) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
  z-index: -1;
  opacity: 0.5;
`;

const AllStepsContainer = styled.div`
  display: grid;
  /* Criando 6 colunas para maior precisão no posicionamento */
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-gap: 20px;
  width: 100%;
  height: 100%;
  position: relative;
  padding: 20px;
  padding-top: 5px;
  opacity: 0;
  transform: scale(0.8);
  
  /* Primeira linha - 3 elementos ocupam 2 colunas cada */
  & > *:nth-child(1) {
    grid-column: 1 / 3;
  }
  
  & > *:nth-child(2) {
    grid-column: 3 / 5;
  }
  
  & > *:nth-child(3) {
    grid-column: 5 / 7;
  }
  
  /* Segunda linha - 2 elementos centralizados */
  & > *:nth-child(4) {
    /* Lembretes: Ocupa o espaço da coluna 2-3 */
    grid-column: 2 / 4;
  }
  
  & > *:nth-child(5) {
    /* Follow Up: Ocupa o espaço da coluna 4-5 */
    grid-column: 4 / 6;
  }
  
  &.visible {
    animation: ${showAllSteps} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(5, 1fr);
    
    & > *:nth-child(n) {
      grid-column: 1;
    }
  }
`;

const OverviewStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 10px;
  background: rgba(0, 10, 20, 0.8);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  grid-column: span 1;
  grid-row: span 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      45deg,
      rgba(0, 255, 255, 0.03) 0px,
      rgba(0, 255, 255, 0.03) 1px,
      transparent 1px,
      transparent 10px
    );
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 255, 255, 0.2);
    border-color: rgba(0, 255, 255, 0.7);
    z-index: 10;
  }
  
  @media (max-width: 768px) {
    grid-column: 1;
  }
`;

const OverviewIconContainer = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(0, 10, 20, 0.8), rgba(0, 20, 40, 0.8));
  border-radius: 50%;
  margin-bottom: 10px;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.3);
  color: #64B6FF;
  font-size: 24px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 50%;
    background: linear-gradient(135deg, #64B6FF, #00FFFF);
    z-index: -1;
    opacity: 0.7;
  }
`;

const OverviewContent = styled.div`
  width: 100%;
  text-align: center;
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 15px;
`;

const OverviewTitle = styled.h3`
  font-size: 1.2em;
  margin-bottom: 0;
  color: #64B6FF;
  font-family: 'Orbitron', sans-serif;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
`;

const OverviewDescription = styled.p`
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

const PresentationSlide20 = ({ scaleFactor = 1.2 }) => {
  const { lang } = useLanguage();
  const t = getTranslation('slide20', lang);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [showAllView, setShowAllView] = useState(false);
  
  // Dados das etapas do processo
  const processSteps = [
    {
      icon: <FaUserCheck />,
      title: t.qualification
    },
    {
      icon: <FaQuestionCircle />,
      title: t.clarification
    },
    {
      icon: <FaCalendarAlt />,
      title: t.scheduling
    },
    {
      icon: <FaBell />,
      title: t.reminders
    },
    {
      icon: <FaRedo />,
      title: t.followUp
    }
  ];
  
  // Manipulador de cliques para avançar para o próximo passo
  const handleSlideClick = () => {
    if (showAllView) {
      // Se estiver na visão geral, volte para o início
      setShowAllView(false);
      setCompletedSteps([]);
      setActiveStepIndex(0);
    } else if (activeStepIndex < processSteps.length - 1) {
      // Se não for o último passo, adicione o atual aos completados
      setCompletedSteps(prev => [...prev, activeStepIndex]);
      setActiveStepIndex(prev => prev + 1);
    } else {
      // Se for o último passo, mostre a visão geral
      setShowAllView(true);
    }
  };
  
  // Função para gerar partículas de dados aleatórias
  const renderDataParticles = (count, containerIndex) => {
    const particles = [];
    const colors = ['#00FFFF', '#64B6FF', '#0080FF', '#FFFFFF'];
    
    for (let i = 0; i < count; i++) {
      const top = `${Math.random() * 100}%`;
      const left = `${Math.random() * 100}%`;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const duration = `${1 + Math.random() * 2}s`;
      const delay = `${Math.random() * 2}s`;
      
      particles.push(
        <DataParticle 
          key={`particle-${containerIndex}-${i}`}
          top={top}
          left={left}
          color={color}
          duration={duration}
          delay={delay}
        />
      );
    }
    
    return particles;
  };

  // Função para gerar círculos tecnológicos decorativos
  const renderTechCircles = () => {
    const circles = [];
    const sizes = ['15px', '20px', '25px', '30px', '40px'];
    const positions = [
      { top: '10%', left: '5%' },
      { top: '20%', left: '80%' },
      { top: '40%', left: '90%' },
      { top: '60%', left: '5%' },
      { top: '80%', left: '85%' },
      { top: '90%', left: '20%' }
    ];
    
    positions.forEach((pos, index) => {
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      const opacity = (0.2 + Math.random() * 0.3).toFixed(2);
      circles.push(
        <TechCircle 
          key={`circle-${index}`}
          top={pos.top}
          left={pos.left}
          size={size}
          opacity={opacity}
        />
      );
    });
    
    return circles;
  };

  // Função para gerar pontos de dados
  const renderDataPoints = (stepIndex) => {
    const points = [];
    const count = 5 + Math.floor(Math.random() * 3);
    
    for (let i = 0; i < count; i++) {
      const size = `${3 + Math.random() * 2}px`;
      const top = `${10 + Math.random() * 80}%`;
      const left = `${10 + Math.random() * 80}%`;
      
      points.push(
        <DataPoint 
          key={`point-${stepIndex}-${i}`}
          size={size}
          top={top}
          left={left}
        />
      );
    }
    
    return points;
  };
  
  // Definir variáveis CSS personalizadas para posições de miniatura
  const getStepStyle = (index) => {
    const targetPositions = [
      { x: '2%', y: '80%' },
      { x: '22%', y: '80%' },
      { x: '42%', y: '80%' },
      { x: '62%', y: '80%' },
      { x: '82%', y: '80%' }
    ];
    
    const pos = targetPositions[index];
    return {
      '--target-x': pos.x,
      '--target-y': pos.y
    };
  };
  
  // Define qual classe de estilo deve ser aplicada a cada passo
  const getStepClass = (index) => {
    if (showAllView) {
      return 'hidden'; // Oculta todos os passos quando estamos na visão geral
    } else if (index === activeStepIndex) {
      return 'fullscreen';
    } else if (completedSteps.includes(index)) {
      return 'miniature';
    } else {
      return 'hidden';
    }
  };
  
  // Efeito para configurar o primeiro passo como visível após o primeiro render
  React.useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, [isFirstRender]);

  // Set the scale factor on mount
  React.useEffect(() => {
    document.documentElement.style.setProperty('--scale-factor', scaleFactor);
  }, [scaleFactor]);
  
  return (
    <SlideWrapper onClick={handleSlideClick}>
      <ScalableContent>
        <GridContainer />
        <SlideTitle>{t.title}</SlideTitle>
        
        {showAllView ? (
          <ContentPreview>
            <StyledProcessSection>
              <AllStepsContainer className="visible">
                {processSteps.map((step, index) => (
                  <OverviewStepContainer key={`overview-${index}`}>
                    {renderDataParticles(3, `overview-${index}`)}
                    <OverviewContent>
                      <OverviewIconContainer>
                        {step.icon}
                      </OverviewIconContainer>
                      <OverviewTitle>{step.title}</OverviewTitle>
                    </OverviewContent>
                  </OverviewStepContainer>
                ))}
              </AllStepsContainer>
            </StyledProcessSection>
          </ContentPreview>
        ) : (
          <ContentPreview>
            <StyledProcessSection>
              
                {/* Primeira coluna - Step ativo */}
                
                  {processSteps.map((step, index) => {
                    // Determina a classe do container baseado no estado
                    const containerClass = 
                      index === activeStepIndex ? "fullscreen" :
                      completedSteps.includes(index) ? "miniature" : "hidden";
                    
                    // Calcula a posição para miniatura
                    const targetX = index === activeStepIndex - 1 ? "-110%" : "110%";
                    const targetY = "-10%";
                    
                    return (
                      <StyledStepContainer 
                        key={index}
                        className={containerClass}
                        style={{
                          '--target-x': targetX,
                          '--target-y': targetY,
                          visibility: containerClass === "hidden" ? "hidden" : "visible"
                        }}
                      >
                        {renderDataParticles(15, index)}
                        {renderTechCircles()}
                        <StepContent>
                          <StepIconContainer>
                            <StepIcon>{step.icon}</StepIcon>
                          </StepIconContainer>
                          <StepTitle data-text={step.title}>{step.title}</StepTitle>
                        </StepContent>
                        <StepCorner position="topLeft" />
                        <StepCorner position="topRight" />
                        <StepCorner position="bottomLeft" />
                        <StepCorner position="bottomRight" />
                        
                        {/* Pontos de dados decorativos */}
                        <DataPoint top="15%" left="10%" delay="0.2s" />
                        <DataPoint top="25%" left="80%" delay="0.5s" />
                        <DataPoint top="60%" left="5%" delay="0.3s" />
                        <DataPoint top="75%" left="92%" delay="0.7s" />
                        <DataPoint top="85%" left="20%" delay="0.1s" />
                      </StyledStepContainer>
                    );
                  })}
                
              
            </StyledProcessSection>
          </ContentPreview>
        )}
        
        <ProgressIndicator>
          {showAllView ? t.completeView : `${t.step} ${activeStepIndex + 1}/${processSteps.length}`}
        </ProgressIndicator>
      </ScalableContent>
    </SlideWrapper>
  );
};

export default PresentationSlide20;
